// Application Constants
export const APP_CONFIG = {
  name: 'Kanav Sharma Portfolio',
  version: '2.0.0',
  description:
    'Kanav Sharma, Senior Technical Lead at SearchUnify. Javascript full stack. Work on AI Agent Partner, Agent Helper, and SearchUnify core.',
  author: 'Kanav Sharma',
  url: 'https://kanavs.web.app',
  linkedIn: 'https://www.linkedin.com/in/sharma-kanav',
  jsGaming: 'https://danted4.github.io/jsGaming/',
  resumeFileName: 'Resume_Kanav.pdf',
};

// Responsive Breakpoints (keeping your existing mobile breakpoint)
export const RESPONSIVE = {
  mobile: 768, // Your existing breakpoint
  tablet: 1024,
  desktop: 1280,

  // Media query helpers
  isMobile: () => window.innerWidth <= RESPONSIVE.mobile,
  isTablet: () => window.innerWidth > RESPONSIVE.mobile && window.innerWidth <= RESPONSIVE.tablet,
  isDesktop: () => window.innerWidth > RESPONSIVE.tablet,
};

// Portfolio Assets Configuration
export const NON_AI_PROJECTS = [
  {
    id: 't90-tank',
    name: 't90.gif',
    title: 'T90',
    description: `A classic that you might've played in early days. The game focuses on bullet tracking, destructible objects, collisions (tanks with tanks, tanks with bricks, bullets with tanks). The main highlight of the game is the functions tracking the movement and logic moving the enemies.`,
    category: 'game',
    technologies: ['JavaScript', 'Canvas API', 'Collision Detection'],
    url: 'https://danted4.github.io/jsGaming/',
    githubUrl: 'https://github.com/danted4/jsGaming',
    aiGenerated: false,
  },
  {
    id: 'brick-breaker',
    name: 'brick.gif',
    title: 'Brick Breaker',
    description: `Another one of the most famous games. The legendary brick breaker. This game's loop is constantly checking the ball's collision with bottom limit of the y-axis. If it's not blocked by the bar, the game is over. Bricks are destructible objects and both horizontal and vertical velocities are at play.`,
    category: 'game',
    technologies: ['JavaScript', 'Canvas API', 'Game Physics'],
    url: 'https://danted4.github.io/jsGaming/',
    githubUrl: 'https://github.com/danted4/jsGaming',
    aiGenerated: false,
  },
];

export const AI_PROJECTS = [
  {
    id: 'code-automata',
    title: 'Code-Automataᵀᴹ',
    name: 'code-automata.webm',
    description: `CLI and workflow tool for AI-assisted coding with Cursor & AmpCode integration and automation. Streamlines development workflows and agentic coding tasks.`,
    category: 'tool',
    technologies: ['Node.js', 'CLI', 'Cursor', 'Automation'],
    url: 'https://github.com/danted4/Code-Automata/releases',
    githubUrl: 'https://github.com/danted4/Code-Automata',
    aiGenerated: true,
  },
  {
    id: 'vector-mcp',
    name: 'vector-mcp.gif',
    title: 'Vector MCP',
    description: `A semantic code search and indexing system that enhances AI coding workflows. Features intelligent codebase indexing with MongoDB vector storage, Ollama embeddings for local AI processing, and real-time delta updates. Designed to provide context-aware code retrieval for AI assistants like Claude Code.`,
    category: 'tool',
    technologies: ['Node.js', 'MongoDB', 'Ollama', 'Vector Search', 'AI Integration'],
    url: 'https://github.com/danted4/vector-mcp',
    githubUrl: 'https://github.com/danted4/vector-mcp',
    aiGenerated: true,
  },
  {
    id: 'paintshop',
    name: 'paintshop.gif',
    title: 'PaintShop',
    description: `A modern image editing application that combines the simplicity of MS Paint with the power of Photoshop. Built with Preact for optimal performance, featuring layers, filters, drawing tools, and professional image manipulation capabilities.`,
    category: 'app',
    technologies: ['Preact', 'Canvas API', 'Image Processing', 'Modern UI'],
    url: 'https://danted4.github.io/paintshop/',
    githubUrl: 'https://github.com/danted4/paintshop',
    aiGenerated: true,
  },
  {
    id: 'jotty',
    name: 'jotty.gif',
    title: 'Jotty',
    description: `A feature-rich notes application built with Preact + Vite, featuring local storage persistence, code blocks with syntax highlighting, themes, and mobile-friendly design. Includes auto-save, search functionality, note templates, and image support.`,
    category: 'app',
    technologies: ['Preact', 'Vite', 'PrismJS', 'Local Storage', 'Modern UI'],
    url: 'https://danted4.github.io/jotty/',
    githubUrl: 'https://github.com/danted4/jotty',
    aiGenerated: true,
  },
];

// Combined portfolio assets for backward compatibility
export const PORTFOLIO_ASSETS = [...NON_AI_PROJECTS, ...AI_PROJECTS];

// Animation Configurations
export const ANIMATIONS = {
  // Loading animations
  bounce: {
    duration: '1.2s',
    timingFunction: 'ease-in-out',
    iterationCount: 'infinite',
  },

  // PlayStation icons animation
  playStation: {
    duration: '4s',
    timingFunction: 'linear',
    iterationCount: 'infinite',
    delayIncrement: 1, // seconds between each icon
  },

  // Page transitions
  pageTransition: {
    duration: '300ms',
    timingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },

  // Modal animations
  modal: {
    duration: '200ms',
    timingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
};

// PDF Viewer Configuration
export const PDF_CONFIG = {
  // URL parameters for PDF embed optimization
  embedParams: {
    toolbar: 0, // Hide toolbar
    navpanes: 0, // Hide navigation panels
    scrollbar: 0, // Hide scrollbars
    view: 'FitH', // Fit horizontally
  },

  // Generate PDF URL with parameters
  generatePdfUrl: (baseUrl) => {
    const params = Object.entries(PDF_CONFIG.embedParams)
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
    return `${baseUrl}#${params}`;
  },
};

// Navigation Configuration
export const NAVIGATION = {
  items: [
    { id: 'projects', label: 'projects', href: '#projects' },
    { id: 'work', label: 'work', href: '#work' },
    { id: 'contact', label: 'contact', href: '#contact' },
  ],
};

// Error Messages
export const ERROR_MESSAGES = {
  // Asset loading errors
  assetLoadFailed: 'Failed to load portfolio assets. Please try refreshing the page.',

  // PDF errors
  pdfLoadFailed: 'Unable to load resume preview. Please try downloading instead.',
  downloadFailed: 'Download failed. Please try again.',

  // Network errors
  networkError: 'Network connection error. Please check your internet connection.',

  // Generic errors
  genericError: 'Something went wrong. Please try again.',

  // Development errors (should not appear in production)
  devError: 'Development error occurred. Check console for details.',
};

// Success Messages
export const SUCCESS_MESSAGES = {
  downloadStarted: 'Download started successfully',
  assetLoaded: 'Assets loaded successfully',
  previewLoaded: 'Resume preview loaded',
};

// Loading Messages
export const LOADING_MESSAGES = {
  downloading: 'Downloading.....',
  loading: 'Loading...',
  previewLoading: 'Loading resume preview...',
  assetsLoading: 'Loading portfolio assets...',
};

// Accessibility Configuration
export const A11Y = {
  // ARIA labels
  labels: {
    resumePreview: 'Resume preview modal',
    downloadResume: 'Download resume PDF',
    closeModal: 'Close modal',
    portfolioSection: 'Portfolio showcase',
    navigationIcon: (label) => `Navigate to ${label}`,
    gamePreview: (title) => `${title} preview`,
    linkedIn: 'Kanav Sharma on LinkedIn',
    expandWork: 'Expand work history tree',
    collapseWork: 'Collapse work history tree',
  },

  // Keyboard shortcuts
  shortcuts: {
    closeModal: 'Escape',
    downloadResume: 'Ctrl+D',
    togglePreview: 'Space',
  },
};

// Performance Configuration
export const PERFORMANCE = {
  // Asset preloading
  preloadTimeout: 10000, // 10 seconds

  // Image loading
  lazyLoadOffset: 100, // pixels

  // Animation performance
  preferReducedMotion: () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,

  // Debounce timings
  debounce: {
    resize: 150,
    scroll: 16,
    search: 300,
  },
};
